import * as React from "react";

import assetUrl from "../utils/assetUrl";
import {
  getLatestUpdates,
  setLatestUpdates,
  HOME_LATEST_UPDATES_UPDATED_EVENT,
  HOME_LATEST_UPDATES_STORAGE_KEY,
} from "../utils/latestUpdatesStore";

const EMPTY_FORM = {
  title: "",
  subLine1: "",
  subLine2: "",
  sub: "",
  alt: "",
  image: "",
  tag: "",
  tagLink: "",
  link: "",
  date: "",
  titleWeight: "",
  subWeight: "",
  titleFont: "",
  subFont: "",
  titleColor: "",
  swapColors: false,
};

function getTimestamp() {
  const now = new Date();
  const date = now.toISOString().slice(0, 10);
  const time = now.toTimeString().slice(0, 8).replace(/:/g, "-");
  return `${date}_${time}`;
}

function downloadBackupJS(filename, varName, data) {
  if (typeof window === "undefined") return;
  const content = `// Travel Rethink Ways backup\n// Generated at: ${new Date().toISOString()}\nwindow.${varName} = ${JSON.stringify(data, null, 2)};\n`;
  const blob = new Blob([content], { type: "text/javascript;charset=utf-8" });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${filename}-${getTimestamp()}.js`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
}

function normalizeImage(value) {
  const raw = String(value || "").trim();
  if (!raw) return "";
  if (/^https?:\/\//i.test(raw) || raw.startsWith("data:")) return raw;
  if (raw.startsWith("/")) return raw;
  return raw;
}

function denormalizeImageForInput(value) {
  const raw = String(value || "").trim();
  if (!raw) return "";

  const base = import.meta.env.BASE_URL || "/";
  const normalizedBase = base.endsWith("/") ? base : `${base}/`;

  if (!raw.startsWith(normalizedBase)) return raw;
  const rest = raw.slice(normalizedBase.length);
  if (rest.startsWith("assets/")) return `/${rest}`;
  return raw;
}

function buildId() {
  if (typeof window !== "undefined" && window.crypto?.randomUUID) return window.crypto.randomUUID();
  return `lu_${Date.now()}_${Math.random().toString(16).slice(2)}`;
}

export default function LatestUpdatesManager() {
  const [posts, setPosts] = React.useState(() => getLatestUpdates({ includeStoredOverrides: true }));
  const [form, setForm] = React.useState(EMPTY_FORM);
  const [editingId, setEditingId] = React.useState("");
  const [notice, setNotice] = React.useState(null);
  const fileInputRef = React.useRef(null);

  const showNotice = (type, text) => setNotice({ type, text });

  React.useEffect(() => {
    if (!notice) return;
    const timer = window.setTimeout(() => setNotice(null), 2400);
    return () => window.clearTimeout(timer);
  }, [notice]);

  React.useEffect(() => {
    const refresh = () => setPosts(getLatestUpdates({ includeStoredOverrides: true }));
    window.addEventListener("storage", refresh);
    window.addEventListener("focus", refresh);
    window.addEventListener("pageshow", refresh);
    window.addEventListener(HOME_LATEST_UPDATES_UPDATED_EVENT, refresh);
    return () => {
      window.removeEventListener("storage", refresh);
      window.removeEventListener("focus", refresh);
      window.removeEventListener("pageshow", refresh);
      window.removeEventListener(HOME_LATEST_UPDATES_UPDATED_EVENT, refresh);
    };
  }, []);

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setEditingId("");
  };

  const saveAll = (next) => {
    setLatestUpdates(next);
    setPosts(next);
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const link = String(form.link || "").trim();
    const image = normalizeImage(form.image);
    const title = String(form.title || "").trim();
    if (!title) return showNotice("error", "Title is required.");
    if (!link) return showNotice("error", "Post link is required.");
    if (!image) return showNotice("error", "Image URL/path is required.");

    const payload = {
      ...form,
      title,
      link,
      image,
      alt: String(form.alt || "").trim() || title,
      tag: String(form.tag || "").trim(),
      tagLink: String(form.tagLink || "").trim(),
      date: String(form.date || "").trim(),
      sub: String(form.sub || "").trim(),
      subLine1: String(form.subLine1 || "").trim(),
      subLine2: String(form.subLine2 || "").trim(),
      titleWeight: form.titleWeight === "" ? undefined : Number(form.titleWeight),
      subWeight: form.subWeight === "" ? undefined : Number(form.subWeight),
      titleFont: String(form.titleFont || "").trim() || undefined,
      subFont: String(form.subFont || "").trim() || undefined,
      titleColor: String(form.titleColor || "").trim() || undefined,
      swapColors: Boolean(form.swapColors),
    };

    const cleaned = Object.fromEntries(Object.entries(payload).filter(([, v]) => v !== undefined));

    if (editingId) {
      const next = posts.map((p) => (p.id === editingId ? { ...p, ...cleaned } : p));
      saveAll(next);
      showNotice("success", "Latest update updated.");
      return;
    }

    const next = [{ id: buildId(), ...cleaned }, ...posts];
    saveAll(next);
    showNotice("success", "Latest update added.");
    resetForm();
  };

  const startEdit = (post) => {
    setEditingId(post.id);
    setForm({
      ...EMPTY_FORM,
      ...post,
      image: denormalizeImageForInput(post.image),
      titleWeight: post.titleWeight ?? "",
      subWeight: post.subWeight ?? "",
    });
    showNotice("info", "Editing item.");
  };

  const deletePost = (id) => {
    if (!window.confirm("Delete this latest update item?")) return;
    const next = posts.filter((p) => p.id !== id);
    saveAll(next);
    if (editingId === id) resetForm();
    showNotice("success", "Deleted.");
  };

  const move = (id, direction) => {
    const index = posts.findIndex((p) => p.id === id);
    if (index < 0) return;
    const nextIndex = index + direction;
    if (nextIndex < 0 || nextIndex >= posts.length) return;
    const next = posts.slice();
    const [item] = next.splice(index, 1);
    next.splice(nextIndex, 0, item);
    saveAll(next);
  };

  const downloadBackup = () => downloadBackupJS("home-latest-updates-backup", "TRW_HOME_LATEST_UPDATES", posts);

  const resetToDefault = () => {
    if (!window.confirm("Reset to default Latest Updates? This clears local overrides.")) return;
    window.localStorage.removeItem(HOME_LATEST_UPDATES_STORAGE_KEY);
    window.dispatchEvent(new Event(HOME_LATEST_UPDATES_UPDATED_EVENT));
    showNotice("success", "Reset to default.");
  };

  const NOTICE_CLS = {
    success: "border-[#bbf7d0] bg-[#f0fdf4] text-[#166534]",
    error: "border-[#fecaca] bg-[#fff5f5] text-[#991b1b]",
    info: "border-[#bfdbfe] bg-[#eff6ff] text-[#1d4ed8]",
  };

  return (
    <div className="grid grid-cols-1 gap-7 lg:grid-cols-[minmax(0,1.15fr)_minmax(0,0.85fr)]">
      <section className="rounded-[20px] border border-[#d9d9d9] bg-white p-6 shadow-[0_20px_55px_rgba(0,0,0,0.07)]">
        <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-lg font-bold text-black">Home Page Latest Updates Manager</h2>
            <p className="mt-1 text-sm text-[#4b5563]">Controls the “Latest Updates” section on the Home page via local storage (no deploy required).</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button type="button" onClick={downloadBackup} className="rounded-full border border-black px-5 py-2 text-xs font-semibold tracking-[0.4px] uppercase text-black hover:bg-black hover:text-white">Download Backup</button>
            <button type="button" onClick={resetToDefault} className="rounded-full border border-[#dc2626] px-5 py-2 text-xs font-semibold tracking-[0.4px] uppercase text-[#dc2626] hover:bg-[#dc2626] hover:text-white">Reset Default</button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <label className="text-sm font-semibold text-[#1f2937]">Title *
              <input value={form.title} onChange={(e) => setForm((c) => ({ ...c, title: e.target.value }))} className="mt-2 w-full rounded-xl border border-[#d1d5db] px-4 py-3 text-sm outline-none focus:border-[#111827]" />
            </label>
            <label className="text-sm font-semibold text-[#1f2937]">Date (e.g. 29/May/2026)
              <input value={form.date} onChange={(e) => setForm((c) => ({ ...c, date: e.target.value }))} className="mt-2 w-full rounded-xl border border-[#d1d5db] px-4 py-3 text-sm outline-none focus:border-[#111827]" />
            </label>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <label className="text-sm font-semibold text-[#1f2937]">Sub Line 1
              <input value={form.subLine1} onChange={(e) => setForm((c) => ({ ...c, subLine1: e.target.value }))} className="mt-2 w-full rounded-xl border border-[#d1d5db] px-4 py-3 text-sm outline-none focus:border-[#111827]" />
            </label>
            <label className="text-sm font-semibold text-[#1f2937]">Sub Line 2
              <input value={form.subLine2} onChange={(e) => setForm((c) => ({ ...c, subLine2: e.target.value }))} className="mt-2 w-full rounded-xl border border-[#d1d5db] px-4 py-3 text-sm outline-none focus:border-[#111827]" />
            </label>
          </div>

          <label className="block text-sm font-semibold text-[#1f2937]">Single-line Sub (mobile card)
            <input value={form.sub} onChange={(e) => setForm((c) => ({ ...c, sub: e.target.value }))} className="mt-2 w-full rounded-xl border border-[#d1d5db] px-4 py-3 text-sm outline-none focus:border-[#111827]" />
          </label>

          <label className="block text-sm font-semibold text-[#1f2937]">Image URL or /assets path *
            <div className="mt-2 flex flex-col gap-2 md:flex-row md:items-center">
              <input
                value={form.image}
                onChange={(e) => setForm((c) => ({ ...c, image: e.target.value }))}
                placeholder="/assets/images/home/..."
                className="w-full rounded-xl border border-[#d1d5db] px-4 py-3 text-sm outline-none focus:border-[#111827]"
              />
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  const reader = new FileReader();
                  reader.onload = () => {
                    const dataUrl = typeof reader.result === "string" ? reader.result : "";
                    if (!dataUrl) return;
                    setForm((c) => ({ ...c, image: dataUrl }));
                    showNotice("success", "Image loaded from file.");
                  };
                  reader.readAsDataURL(file);
                }}
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="rounded-full border border-black px-4 py-2 text-xs font-semibold tracking-[0.4px] uppercase text-black hover:bg-black hover:text-white"
              >
                Upload File
              </button>
            </div>
            <p className="mt-1 text-xs font-normal text-[#6b7280]">Tip: use `/assets/...`, paste a full `https://...`, or upload a file (saved as a `data:` URL).</p>
          </label>

          <label className="block text-sm font-semibold text-[#1f2937]">Alt Text
            <input value={form.alt} onChange={(e) => setForm((c) => ({ ...c, alt: e.target.value }))} className="mt-2 w-full rounded-xl border border-[#d1d5db] px-4 py-3 text-sm outline-none focus:border-[#111827]" />
          </label>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <label className="text-sm font-semibold text-[#1f2937]">Tag
              <input value={form.tag} onChange={(e) => setForm((c) => ({ ...c, tag: e.target.value }))} className="mt-2 w-full rounded-xl border border-[#d1d5db] px-4 py-3 text-sm outline-none focus:border-[#111827]" />
            </label>
            <label className="text-sm font-semibold text-[#1f2937]">Tag Link
              <input value={form.tagLink} onChange={(e) => setForm((c) => ({ ...c, tagLink: e.target.value }))} className="mt-2 w-full rounded-xl border border-[#d1d5db] px-4 py-3 text-sm outline-none focus:border-[#111827]" />
            </label>
          </div>

          <label className="block text-sm font-semibold text-[#1f2937]">Post Link *
            <input value={form.link} onChange={(e) => setForm((c) => ({ ...c, link: e.target.value }))} placeholder="https://travel.rethinkways.com/..." className="mt-2 w-full rounded-xl border border-[#d1d5db] px-4 py-3 text-sm outline-none focus:border-[#111827]" />
          </label>

          <div className="rounded-2xl border border-[#d1d5db] p-4">
            <p className="text-sm font-semibold text-[#1f2937]">Optional Style Overrides</p>
            <div className="mt-3 grid grid-cols-1 gap-4 md:grid-cols-3">
              <label className="text-xs font-semibold text-[#1f2937]">Title Weight
                <input type="number" min="100" max="900" value={form.titleWeight} onChange={(e) => setForm((c) => ({ ...c, titleWeight: e.target.value }))} className="mt-2 w-full rounded-xl border border-[#d1d5db] px-3 py-2 text-sm outline-none focus:border-[#111827]" />
              </label>
              <label className="text-xs font-semibold text-[#1f2937]">Sub Weight
                <input type="number" min="100" max="900" value={form.subWeight} onChange={(e) => setForm((c) => ({ ...c, subWeight: e.target.value }))} className="mt-2 w-full rounded-xl border border-[#d1d5db] px-3 py-2 text-sm outline-none focus:border-[#111827]" />
              </label>
              <label className="text-xs font-semibold text-[#1f2937]">Title Color
                <input value={form.titleColor} onChange={(e) => setForm((c) => ({ ...c, titleColor: e.target.value }))} placeholder="#ffffff" className="mt-2 w-full rounded-xl border border-[#d1d5db] px-3 py-2 text-sm outline-none focus:border-[#111827]" />
              </label>
            </div>
            <div className="mt-3 grid grid-cols-1 gap-4 md:grid-cols-2">
              <label className="text-xs font-semibold text-[#1f2937]">Title Font
                <input value={form.titleFont} onChange={(e) => setForm((c) => ({ ...c, titleFont: e.target.value }))} placeholder="Poppins, sans-serif" className="mt-2 w-full rounded-xl border border-[#d1d5db] px-3 py-2 text-sm outline-none focus:border-[#111827]" />
              </label>
              <label className="text-xs font-semibold text-[#1f2937]">Sub Font
                <input value={form.subFont} onChange={(e) => setForm((c) => ({ ...c, subFont: e.target.value }))} placeholder="'Yeseva One', Georgia, serif" className="mt-2 w-full rounded-xl border border-[#d1d5db] px-3 py-2 text-sm outline-none focus:border-[#111827]" />
              </label>
            </div>
            <label className="mt-3 flex items-center gap-2 text-xs font-semibold text-[#1f2937]">
              <input type="checkbox" checked={form.swapColors} onChange={(e) => setForm((c) => ({ ...c, swapColors: e.target.checked }))} />
              Swap Title/Sub colors (matches existing layout)
            </label>
          </div>

          <div className="flex flex-wrap gap-3">
            <button type="submit" className="rounded-full bg-[#e01919] px-7 py-3 text-xs font-semibold tracking-[0.4px] uppercase text-white hover:bg-black">
              {editingId ? "Update Item" : "Add Item"}
            </button>
            <button type="button" onClick={resetForm} className="rounded-full border border-black px-7 py-3 text-xs font-semibold tracking-[0.4px] uppercase text-black hover:bg-black hover:text-white">
              Clear
            </button>
          </div>
        </form>
      </section>

      <section className="rounded-[20px] border border-[#d9d9d9] bg-white p-6 shadow-[0_20px_55px_rgba(0,0,0,0.07)]">
        <h3 className="mb-4 text-lg font-bold text-black">Current Items ({posts.length})</h3>
        <div className="space-y-3">
          {posts.map((post, idx) => (
            <div key={post.id || idx} className="rounded-2xl border border-[#e5e7eb] bg-[#f9fafb] p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="truncate text-sm font-bold text-black">{post.title}</p>
                  <p className="mt-1 truncate text-xs text-[#4b5563]">{post.link}</p>
                  <p className="mt-1 text-[11px] font-semibold uppercase tracking-[0.35px] text-[#6b7280]">{post.date || "No date"}</p>
                </div>
                <div className="flex flex-wrap justify-end gap-2">
                  <button type="button" onClick={() => move(post.id, -1)} className="rounded-full border border-[#d1d5db] px-3 py-1 text-[11px] font-semibold uppercase text-black hover:bg-black hover:text-white">Up</button>
                  <button type="button" onClick={() => move(post.id, 1)} className="rounded-full border border-[#d1d5db] px-3 py-1 text-[11px] font-semibold uppercase text-black hover:bg-black hover:text-white">Down</button>
                  <button type="button" onClick={() => startEdit(post)} className="rounded-full border border-black px-3 py-1 text-[11px] font-semibold uppercase text-black hover:bg-black hover:text-white">Edit</button>
                  <button type="button" onClick={() => deletePost(post.id)} className="rounded-full border border-[#dc2626] px-3 py-1 text-[11px] font-semibold uppercase text-[#dc2626] hover:bg-[#dc2626] hover:text-white">Delete</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {notice && (
        <div className="fixed inset-0 z-[100000] flex items-center justify-center bg-black/45 p-4">
          <div role="alert" aria-live="assertive" className={`w-full max-w-[420px] rounded-2xl border px-5 py-4 shadow-[0_24px_60px_rgba(0,0,0,0.3)] ${NOTICE_CLS[notice.type] || NOTICE_CLS.info}`}>
            <p className="text-[11px] font-semibold uppercase">{notice.type}</p>
            <p className="mt-2 text-sm font-semibold">{notice.text}</p>
            <div className="mt-4 flex justify-end">
              <button type="button" onClick={() => setNotice(null)} className="rounded-full border border-current px-4 py-1.5 text-[11px] font-semibold uppercase">OK</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
